import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// User-preference store (Sidebar spec §3 Persistence): small per-user UI
// choices that survive a reload. Each preference is keyed by the signed-in
// user (or `anon` with no session) so two people on one browser do not share
// a layout. Open sidebar groups are deliberately *not* stored: on load the
// group for the current route opens (§3).

export const ANON_USER = 'anon'

interface UserPrefs {
  sidebarCollapsed: boolean
}

interface PrefsState {
  users: Record<string, Partial<UserPrefs>>
  setSidebarCollapsed: (userKey: string, collapsed: boolean) => void
}

export const usePrefsStore = create<PrefsState>()(
  persist(
    (set) => ({
      users: {},
      setSidebarCollapsed: (userKey, collapsed) =>
        set((s) => ({ users: { ...s.users, [userKey]: { ...(s.users[userKey] ?? {}), sidebarCollapsed: collapsed } } })),
    }),
    { name: 'qtz-prefs' }
  )
)

// `undefined` until the user has chosen, so the caller can pick a default per
// viewport (expanded on desktop, collapsed in the phone strip).
export function useSidebarCollapsed(userKey: string): [boolean | undefined, (collapsed: boolean) => void] {
  const collapsed = usePrefsStore((s) => s.users[userKey]?.sidebarCollapsed)
  const setCollapsed = usePrefsStore((s) => s.setSidebarCollapsed)
  return [collapsed, (c) => setCollapsed(userKey, c)]
}
